<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  scenario: {
    type: Object,
    required: true,
  },
  templateMeta: {
    type: Object,
    required: true,
  },
  fieldOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['edit', 'delete']);

const fieldLabelMap = computed(() =>
  props.fieldOptions.reduce((acc, field) => {
    acc[field.key] = field.label || field.key;
    return acc;
  }, {})
);

const visibleTriggers = computed(() =>
  (props.scenario.trigger_conditions || []).slice(0, 3)
);

const remainingTriggerCount = computed(() =>
  Math.max((props.scenario.trigger_conditions || []).length - 3, 0)
);

const visibleFields = computed(() =>
  (props.scenario.selected_field_keys || []).slice(0, 4)
);

const remainingFieldCount = computed(() =>
  Math.max((props.scenario.selected_field_keys || []).length - 4, 0)
);

</script>

<template>
  <div
    class="rounded-2xl border border-n-slate-4 bg-white dark:bg-n-slate-8 p-4 shadow-sm"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center flex-wrap gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ scenario.title }}
          </h4>
          <span
            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
            :class="
              scenario.enabled
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-n-slate-2 text-n-slate-10 dark:bg-n-slate-7 dark:text-n-slate-4'
            "
          >
            {{ scenario.enabled ? '启用中' : '已停用' }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <Button label="编辑" xs ghost slate @click="emit('edit', scenario)" />
        <Button
          icon="i-lucide-trash"
          xs
          ghost
          slate
          @click="emit('delete', scenario.id)"
        />
      </div>
    </div>

    <div class="mt-4 flex flex-col gap-3">
      <div>
        <div class="text-xs text-n-slate-10 mb-2">触发条件</div>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="item in visibleTriggers"
            :key="item"
            class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
          >
            {{ item }}
          </span>
          <span
            v-if="remainingTriggerCount > 0"
            class="inline-flex items-center rounded-md bg-n-slate-2 px-2 py-1 text-xs text-n-slate-10 dark:bg-n-slate-7 dark:text-n-slate-4"
          >
            +{{ remainingTriggerCount }}
          </span>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3">
        <div class="rounded-xl bg-n-slate-2/80 dark:bg-n-slate-7/50 px-3 py-3">
          <div class="text-xs text-n-slate-10 mb-2">收集字段</div>
          <div v-if="templateMeta.usesFields" class="flex flex-wrap gap-2">
            <span
              v-for="fieldKey in visibleFields"
              :key="fieldKey"
              class="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-n-slate-11 dark:bg-n-slate-8 dark:text-n-slate-3"
            >
              {{ fieldLabelMap[fieldKey] || fieldKey }}
            </span>
            <span
              v-if="remainingFieldCount > 0"
              class="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-n-slate-10 dark:bg-n-slate-8 dark:text-n-slate-4"
            >
              +{{ remainingFieldCount }}
            </span>
            <span
              v-if="!visibleFields.length"
              class="text-xs text-amber-600 dark:text-amber-400"
            >
              未选择字段
            </span>
          </div>
          <div v-else class="text-xs text-n-slate-10">不收集字段</div>
        </div>
      </div>
    </div>
  </div>
</template>
